import { type Action, createActionsHook, createHook, createStore } from 'react-sweet-state';

import { fg } from '@atlaskit/platform-feature-flags';

export type RovoApp = {
	source: string;
	friendlyName: string;
	iconUrl: string;
};
export type FiltererdOrgKnowledge = {
	// enabled with filtered apps, in absence of filtered apps, it's enabled for all apps
	filteredApps: RovoApp[];
};
export type OrgKnowledge = 'disabled' | FiltererdOrgKnowledge;

export type ReasoningMode = 'auto' | 'deep-research' | 'think-deeper';

type ChatModeInternal = {
	reasoningMode: ReasoningMode;
	webSearchEnabled: boolean;
	orgKnowledge: OrgKnowledge;
	autoApplyEnabled: boolean;
	useCurrentPageContext: boolean;
};

export interface ChatMode extends ChatModeInternal {
	/**
	 * Derived from orgKnowledge
	 * appFilters === undefined means all org knowledge applied and no apps filtered.
	 * appFilters === [] means no org knowledge applied at all.
	 * appFilters === [app1, app2, ...] means org knowledge applied and apps filtered.
	 */
	appFilters: RovoApp[] | undefined;
	/**
	 * Derived from reasoningMode === 'deep-research'
	 */
	deepResearchEnabled: boolean;
	/**
	 * Derived from reasoningMode === 'think-deeper'
	 */
	thinkDeeperEnabled: boolean;
	/**
	 * Derived from reasoningMode === 'auto'
	 */
	autoReasoningEnabled: boolean;
}

export type ChatModeState = {
	[key: string]: ChatModeInternal;
};

export const getDerivedDefaultChatMode = (): ChatModeInternal => {
	return {
		...{
			reasoningMode: 'auto',
			webSearchEnabled: false,
			orgKnowledge: {
				filteredApps: [],
			},
			autoApplyEnabled: true,
		},
		useCurrentPageContext: fg('disable_default_on_current_page_as_context') ? false : true,
	};
};

const updateMode: (
	key: string,
	mode: Partial<ChatModeInternal>,
) => Action<ChatModeState, any, any> = (key: string, mode: Partial<ChatModeInternal>) => {
	return ({ setState, getState }) => {
		const state = getState()[key] || getDerivedDefaultChatMode();
		setState({
			[key]: {
				...state,
				...mode,
			},
		});
	};
};

const store = createStore({
	initialState: {} as ChatModeState,
	actions: {
		setReasoningMode:
			(key: string, reasoningMode: ReasoningMode) =>
			({ dispatch }) => {
				dispatch(updateMode(key, { reasoningMode: reasoningMode }));
			},
		resetReasoningMode:
			(key: string) =>
			({ dispatch }) => {
				dispatch(updateMode(key, { reasoningMode: getDerivedDefaultChatMode().reasoningMode }));
			},
		setWebSearchEnabled:
			(key: string, isEnabled: boolean) =>
			({ dispatch }) => {
				dispatch(updateMode(key, { webSearchEnabled: isEnabled }));
			},
		setOrgKnowledge:
			(key: string, orgKnowledge: OrgKnowledge) =>
			({ dispatch }) => {
				dispatch(updateMode(key, { orgKnowledge: orgKnowledge }));
			},
		setUseCurrentPageContext:
			(key: string, isEnabled: boolean) =>
			({ dispatch }) => {
				dispatch(updateMode(key, { useCurrentPageContext: isEnabled }));
			},
		setChatMode:
			(
				key: string,
				chatMode: Partial<
					Omit<ChatMode, 'orgKnowledge' | 'deepResearchEnabled' | 'thinkDeeperEnabled'>
				>,
			) =>
			({ dispatch }) => {
				let updatedChatMode: Partial<ChatMode> = chatMode;
				let orgKnowledge: OrgKnowledge = getDerivedDefaultChatMode().orgKnowledge;
				if (chatMode.appFilters === undefined) {
					orgKnowledge = getDerivedDefaultChatMode().orgKnowledge;
				} else if (chatMode.appFilters.length > 0) {
					orgKnowledge = {
						filteredApps: chatMode.appFilters,
					};
				} else {
					orgKnowledge = 'disabled';
				}
				updatedChatMode = {
					...chatMode,
					orgKnowledge: orgKnowledge,
				};
				dispatch(updateMode(key, updatedChatMode));
			},
		getChatMode:
			(key: string) =>
			({ getState }) => {
				const state = getState()[key] || getDerivedDefaultChatMode();
				return {
					...state,
					...getDerivedReasoningMode(state.reasoningMode),
					appFilters: getAppFilters(state.orgKnowledge),
				};
			},
		clearMode:
			(key: string) =>
			({ dispatch }) => {
				dispatch(updateMode(key, getDerivedDefaultChatMode()));
			},
	},
});

const getDerivedReasoningMode = (reasoningMode: ReasoningMode) => {
	return {
		deepResearchEnabled: reasoningMode === 'deep-research',
		thinkDeeperEnabled: reasoningMode === 'think-deeper',
		autoReasoningEnabled: reasoningMode === 'auto',
	};
};

const getAppFilters = (orgKnowledge: OrgKnowledge) => {
	if (orgKnowledge === 'disabled') {
		// no org knowledge applied at all.
		return [];
	} else if (orgKnowledge.filteredApps.length === 0) {
		// all org knowledge applied and no apps filtered.
		return undefined;
	} else {
		// org knowledge applied and apps filtered.
		return orgKnowledge.filteredApps;
	}
};

const selector = (state: ChatModeState, key: string) => {
	const chatMode = state[key] || getDerivedDefaultChatMode();
	return {
		...chatMode,
		...getDerivedReasoningMode(chatMode.reasoningMode),
		appFilters: getAppFilters(chatMode.orgKnowledge),
	};
};
export const useChatModeStore = createHook(store, {
	selector,
});

export const useChatModeStoreActions = createActionsHook(store);
export const useChatModeState: (key: string) => ChatMode = (key) => {
	const state = useChatModeStore(key)[0];
	return state;
};
